import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Box,
	CircularProgress,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

export interface SystemNotificationReceiverRecord {
	_id: string;
	title: 'Mr.' | 'Miss' | 'Mrs.';
	firstName: string;
	lastName: string;
	whatsappNumber: string;
	mobileNumber: string;
	email?: string;
	createdBy?: string;
	updatedBy?: string;
	createdAt?: string;
	updatedAt?: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	receiver: SystemNotificationReceiverRecord | null;
}

const TITLE_OPTIONS: SystemNotificationReceiverRecord['title'][] = ['Mr.', 'Miss', 'Mrs.'];
const PHONE_PATTERN = /^\+[1-9]\d{1,14}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = {
	title: '' as '' | SystemNotificationReceiverRecord['title'],
	firstName: '',
	lastName: '',
	whatsappNumber: '',
	mobileNumber: '',
	email: '',
};

export const SystemNotificationReceiverDialog: React.FC<Props> = ({
	open,
	onClose,
	onSave,
	receiver,
}) => {
	const isEditing = Boolean(receiver?._id);
	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		if (receiver) {
			setForm({
				title: receiver.title || '',
				firstName: receiver.firstName || '',
				lastName: receiver.lastName || '',
				whatsappNumber: receiver.whatsappNumber || '',
				mobileNumber: receiver.mobileNumber || '',
				email: receiver.email || '',
			});
		} else {
			setForm(EMPTY_FORM);
		}
		setErrors({});
	}, [open, receiver]);

	const validate = (): boolean => {
		const next: Record<string, string> = {};
		if (!form.title) next.title = 'Title is required';
		if (!form.firstName.trim()) next.firstName = 'First Name is required';
		if (!form.lastName.trim()) next.lastName = 'Last Name is required';

		const whatsapp = form.whatsappNumber.trim();
		if (!whatsapp) next.whatsappNumber = 'WhatsApp Number is required';
		else if (!PHONE_PATTERN.test(whatsapp)) {
			next.whatsappNumber = 'Must include country code starting with + (e.g. +441234567890)';
		}

		const mobile = form.mobileNumber.trim();
		if (!mobile) next.mobileNumber = 'Mobile Number is required';
		else if (!PHONE_PATTERN.test(mobile)) {
			next.mobileNumber = 'Must include country code starting with + (e.g. +441234567890)';
		}

		const email = form.email.trim();
		if (email && !EMAIL_PATTERN.test(email)) {
			next.email = 'Please enter a valid email address';
		}

		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;
		setSaving(true);
		try {
			const payload = {
				title: form.title,
				firstName: form.firstName.trim(),
				lastName: form.lastName.trim(),
				whatsappNumber: form.whatsappNumber.trim(),
				mobileNumber: form.mobileNumber.trim(),
				email: form.email.trim(),
			};
			if (isEditing && receiver?._id) {
				await apiClient.put(`/system-notifications/receivers/${receiver._id}`, payload);
			} else {
				await apiClient.post('/system-notifications/receivers/new', payload);
			}
			onSave();
		} catch (error) {
			Logger.error('SystemNotificationReceiverDialog', `Failed to save receiver: ${error}`);
			setErrors({ form: error instanceof Error ? error.message : 'Failed to save receiver' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{isEditing ? 'Edit Receiver' : 'New Receiver'}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2} pt={1}>
					{errors.form && (
						<Typography color="error" variant="body2">{errors.form}</Typography>
					)}
					<FormControl fullWidth size="small" error={Boolean(errors.title)} required>
						<InputLabel>Title</InputLabel>
						<Select
							label="Title"
							value={form.title}
							onChange={(e) =>
								setForm((prev) => ({
									...prev,
									title: e.target.value as SystemNotificationReceiverRecord['title'],
								}))
							}
						>
							{TITLE_OPTIONS.map((opt) => (
								<MenuItem key={opt} value={opt}>
									{opt}
								</MenuItem>
							))}
						</Select>
						{errors.title && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
								{errors.title}
							</Typography>
						)}
					</FormControl>
					<TextField
						label="First Name"
						value={form.firstName}
						onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
						error={Boolean(errors.firstName)}
						helperText={errors.firstName}
						fullWidth
						required
						size="small"
					/>
					<TextField
						label="Last Name"
						value={form.lastName}
						onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
						error={Boolean(errors.lastName)}
						helperText={errors.lastName}
						fullWidth
						required
						size="small"
					/>
					<TextField
						label="WhatsApp Number"
						value={form.whatsappNumber}
						onChange={(e) => setForm((prev) => ({ ...prev, whatsappNumber: e.target.value }))}
						error={Boolean(errors.whatsappNumber)}
						helperText={errors.whatsappNumber || 'Include country code prefix (e.g. +44, +91)'}
						fullWidth
						required
						size="small"
						placeholder="+441234567890"
					/>
					<TextField
						label="Mobile Number"
						value={form.mobileNumber}
						onChange={(e) => setForm((prev) => ({ ...prev, mobileNumber: e.target.value }))}
						error={Boolean(errors.mobileNumber)}
						helperText={errors.mobileNumber || 'Include country code prefix (e.g. +44, +91)'}
						fullWidth
						required
						size="small"
						placeholder="+441234567890"
					/>
					<TextField
						label="Email"
						value={form.email}
						onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
						error={Boolean(errors.email)}
						helperText={errors.email || 'Optional'}
						fullWidth
						size="small"
						type="email"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					color="secondary"
					onClick={onClose}
					disabled={saving}
					startIcon={<Cancel />}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
					variant="contained"
					color="success"
					disabled={saving}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SystemNotificationReceiverDialog;
